import type { Quote, Lead, OrderRequest, OrderDetail } from '@furni/shared';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string; code?: string };
    } | null;
    throw new ApiError(
      body?.error?.message ?? `Request failed: ${res.status}`,
      body?.error?.code ?? 'INTERNAL',
      res.status,
    );
  }
  return res.json() as Promise<T>;
}

export async function analyzeImage(file: File): Promise<Quote> {
  const form = new FormData();
  form.append('image', file);
  const res = await fetch('/api/analyze', { method: 'POST', body: form, credentials: 'include' });
  return handle<Quote>(res);
}

export async function fetchQuote(id: string): Promise<Quote> {
  const res = await fetch(`/api/quote/${encodeURIComponent(id)}`, { credentials: 'include' });
  return handle<Quote>(res);
}

export async function submitLead(lead: Lead): Promise<{ id: string }> {
  const res = await fetch('/api/leads', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(lead),
    credentials: 'include',
  });
  return handle<{ id: string }>(res);
}

export async function submitOrder(order: OrderRequest): Promise<{ id: string }> {
  const res = await fetch('/api/orders', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(order),
    credentials: 'include',
  });
  return handle<{ id: string }>(res);
}

export async function fetchOrder(id: string): Promise<OrderDetail> {
  const res = await fetch(`/api/orders/${encodeURIComponent(id)}`, { credentials: 'include' });
  return handle<OrderDetail>(res);
}
